import React, { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";

function App() {
  const [hijriDate, setHijriDate] = useState("");
  const [englishDate, setEnglishDate] = useState("");
  const [startPage, setStartPage] = useState("");
  const [endPage, setEndPage] = useState("");
  const [message, setMessage] = useState("");
  const [feedback, setFeedback] = useState("");

  const [sessions, setSessions] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  // WhatsApp auth states
  const [authenticated, setAuthenticated] = useState(null);
  const [qrValue, setQrValue] = useState(null);

  // Recipients states
  const [recipients, setRecipients] = useState([]);
  const [recipientFeedback, setRecipientFeedback] = useState("");
  const [editRecipientMode, setEditRecipientMode] = useState(false);
  const [editRecipientId, setEditRecipientId] = useState(null);
  const [countryCode, setCountryCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [active, setActive] = useState(true);

  // Fetch the QR code status
  const handleShowQr = async () => {
    try {
      const response = await fetch(
        "https://quranbackend.matanaseel.com/whatsapp-qr"
      );
      const data = await response.json();
      setAuthenticated(data.authenticated);
      setQrValue(data.qr);
    } catch (error) {
      console.error("Error fetching QR:", error);
    }
  };

  const handleLogout = async () => {
    try {
      const response = await fetch(
        "https://quranbackend.matanaseel.com/whatsapp-logout",
        {
          method: "POST",
        }
      );
      const data = await response.json();
      setAuthenticated(data.authenticated);
      setQrValue(null);
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  useEffect(() => {
    // Calculate the current Hijri date
    const today = new Date();
    const localeFormat = "ar-SA-u-ca-islamic-umalqura";
    const hijriFormatted = new Intl.DateTimeFormat(localeFormat, {
      day: "numeric",
      month: "numeric",
      year: "numeric",
    }).format(today);

    // Set Hijri date state
    setHijriDate(hijriFormatted);

    // Also set the English date (Gregorian)
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    setEnglishDate(`${yyyy}-${mm}-${dd}`);
  }, []);

  useEffect(() => {
    fetchSessions();
    fetchRecipients();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await fetch(
        "https://quranbackend.matanaseel.com/sessions"
      );
      const data = await response.json();
      setSessions(data);
    } catch (error) {
      console.error("Error fetching sessions:", error);
    }
  };

  const fetchRecipients = async () => {
    try {
      const response = await fetch(
        "https://quranbackend.matanaseel.com/recipients"
      );
      const data = await response.json();
      setRecipients(data);
    } catch (error) {
      console.error("Error fetching recipients:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      hijri_date: hijriDate,
      english_date: englishDate,
      start_page: parseInt(startPage, 10),
      end_page: parseInt(endPage, 10),
      message,
    };

    try {
      let response;
      let result;

      if (editMode) {
        // Update existing session
        response = await fetch(
          `https://quranbackend.matanaseel.com/sessions/${editId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
          }
        );
        result = await response.json();
        if (response.ok) {
          setFeedback(`Updated Successfully`);
          clearForm();
        } else {
          setFeedback(`Error: ${result.error}`);
        }
      } else {
        // Insert new session
        response = await fetch("https://quranbackend.matanaseel.com/insert", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        result = await response.json();

        if (response.ok) {
          setFeedback(`Success: ${result.message}`);
          clearForm(false);
        } else {
          setFeedback(`Error: ${result.error}`);
        }
      }

      fetchSessions();
    } catch (error) {
      console.error(error);
      setFeedback("An error occurred.");
    }
  };

  const clearForm = (resetDates = true) => {
    setStartPage("");
    setEndPage("");
    setMessage("");
    setEditMode(false);
    setEditId(null);

    if (resetDates) {
      // Recalculate today’s dates
      const today = new Date();
      const localeFormat = "ar-SA-u-ca-islamic-umalqura";
      const hijriFormatted = new Intl.DateTimeFormat(localeFormat, {
        day: "numeric",
        month: "numeric",
        year: "numeric",
      }).format(today);
      setHijriDate(hijriFormatted);

      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      setEnglishDate(`${yyyy}-${mm}-${dd}`);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(
        `https://quranbackend.matanaseel.com/sessions/${id}`,
        {
          method: "DELETE",
        }
      );
      const result = await response.json();
      if (response.ok) {
        setFeedback("Session deleted successfully");
        fetchSessions();
      } else {
        setFeedback(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error(error);
      setFeedback("An error occurred.");
    }
  };

  const handleEdit = (session) => {
    setEditMode(true);
    setEditId(session.id);
    setHijriDate(session.hijri_date);
    setEnglishDate(session.english_date);
    setStartPage(session.start_page);
    setEndPage(session.end_page);
    setMessage(session.message);
    setFeedback("Editing mode: Modify the fields and Submit");
  };

  // Recipients Handlers
  const handleRecipientSubmit = async (e) => {
    e.preventDefault();
    setRecipientFeedback("");

    const payload = {
      country_code: countryCode,
      phone_number: phoneNumber,
      active: active ? 1 : 0,
    };

    try {
      let response;
      let result;

      if (editRecipientMode) {
        // Update existing recipient
        response = await fetch(
          `https://quranbackend.matanaseel.com/recipients/${editRecipientId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );
        result = await response.json();
        if (response.ok) {
          setRecipientFeedback("Recipient updated successfully");
          clearRecipientForm();
          fetchRecipients();
        } else {
          setRecipientFeedback(`Error: ${result.error}`);
        }
      } else {
        // Create new recipient
        response = await fetch(
          "https://quranbackend.matanaseel.com/recipients",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );
        result = await response.json();
        if (response.ok) {
          setRecipientFeedback("Recipient created successfully");
          clearRecipientForm();
          fetchRecipients();
        } else {
          setRecipientFeedback(`Error: ${result.error}`);
        }
      }
    } catch (error) {
      console.error("Error submitting recipient:", error);
      setRecipientFeedback("An error occurred.");
    }
  };

  const handleEditRecipient = (recipient) => {
    setEditRecipientMode(true);
    setEditRecipientId(recipient.id);
    setCountryCode(recipient.country_code);
    setPhoneNumber(recipient.phone_number);
    setActive(recipient.active === 1);
    setRecipientFeedback("Editing mode: modify fields and submit");
  };

  const handleDeleteRecipient = async (id) => {
    setRecipientFeedback("");
    const response = await fetch(
      `https://quranbackend.matanaseel.com/recipients/${id}`,
      {
        method: "DELETE",
      }
    );
    const result = await response.json();
    if (response.ok) {
      setRecipientFeedback("Recipient deleted successfully");
      fetchRecipients();
    } else {
      setRecipientFeedback(`Error: ${result.error}`);
    }
  };

  const handleCancelRecipientEdit = () => {
    clearRecipientForm();
    setRecipientFeedback("Edit canceled");
  };

  const clearRecipientForm = () => {
    setCountryCode("");
    setPhoneNumber("");
    setActive(true);
    setEditRecipientMode(false);
    setEditRecipientId(null);
  };

  return (
    <>
      <div className="min-h-screen p-4 bg-gray-100">
        <div className="max-w-3xl mx-auto">
          {/* Sessions Section */}
          <div className="bg-white shadow-md rounded-lg p-6 mb-6">
            <h1 className="text-2xl font-bold mb-4 text-center">
              {editMode ? "Edit Reading Session" : "Record Reading Session"}
            </h1>
            {feedback && (
              <p className="text-center mb-4 text-green-600">{feedback}</p>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block mb-1 font-medium text-gray-700">
                  Hijri Date
                </label>
                <input
                  type="text"
                  value={hijriDate}
                  onChange={(e) => setHijriDate(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block mb-1 font-medium text-gray-700">
                  English Date
                </label>
                <input
                  type="date"
                  value={englishDate}
                  onChange={(e) => setEnglishDate(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
                  required
                />
              </div>
              <div className="flex space-x-2">
                <div className="flex-1">
                  <label className="block mb-1 font-medium text-gray-700">
                    Start Page
                  </label>
                  <input
                    type="number"
                    value={startPage}
                    onChange={(e) => setStartPage(e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
                    placeholder="e.g., 10"
                    required
                  />
                </div>
                <div className="flex-1">
                  <label className="block mb-1 font-medium text-gray-700">
                    End Page
                  </label>
                  <input
                    type="number"
                    value={endPage}
                    onChange={(e) => setEndPage(e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
                    placeholder="e.g., 20"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block mb-1 font-medium text-gray-700">
                  Message
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
                  placeholder="Describe what you read..."
                  required
                ></textarea>
              </div>
              <button
                type="submit"
                className="w-full py-2 px-4 bg-blue-600 text-white font-semibold rounded hover:bg-blue-700 transition duration-200"
              >
                {editMode ? "Update" : "Submit"}
              </button>
              {editMode && (
                <button
                  type="button"
                  onClick={() => {
                    clearForm(false);
                    setFeedback("Form reset");
                  }}
                  className="w-full py-2 px-4 bg-gray-600 text-white font-semibold rounded hover:bg-gray-700 transition duration-200 mt-2"
                >
                  Cancel Edit
                </button>
              )}
            </form>
          </div>

          <div className="bg-white shadow-md rounded-lg p-6 mb-6">
            <h2 className="text-xl font-bold mb-4 text-center">
              All Reading Sessions
            </h2>
            {sessions.length === 0 ? (
              <p className="text-center text-gray-500">
                No sessions recorded yet.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-2 text-left">ID</th>
                      <th className="border p-2 text-left">Hijri Date</th>
                      <th className="border p-2 text-left">English Date</th>
                      <th className="border p-2 text-left">Start Page</th>
                      <th className="border p-2 text-left">End Page</th>
                      <th className="border p-2 text-left">Message</th>
                      <th className="border p-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((session) => (
                      <tr key={session.id}>
                        <td className="border p-2">{session.id}</td>
                        <td className="border p-2">{session.hijri_date}</td>
                        <td className="border p-2">{session.english_date}</td>
                        <td className="border p-2">{session.start_page}</td>
                        <td className="border p-2">{session.end_page}</td>
                        <td className="border p-2">{session.message}</td>
                        <td className="border p-2 flex space-x-2 justify-center">
                          <button
                            onClick={() => handleEdit(session)}
                            className="bg-yellow-500 text-white px-2 py-1 rounded hover:bg-yellow-600"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(session.id)}
                            className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Recipients Section */}
          <div className="bg-white shadow-md rounded-lg p-6 mb-6">
            <h2 className="text-xl font-bold mb-4 text-center">
              Manage Recipients
            </h2>
            {recipientFeedback && (
              <p className="mb-4 text-green-600 text-center">
                {recipientFeedback}
              </p>
            )}

            <form
              onSubmit={handleRecipientSubmit}
              className="mb-6 space-y-4 max-w-sm bg-gray-50 p-4 shadow rounded mx-auto"
            >
              <div>
                <label className="block mb-1 font-medium text-gray-700">
                  Country Code
                </label>
                <input
                  type="text"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
                  placeholder="e.g., 1"
                  required
                />
              </div>
              <div>
                <label className="block mb-1 font-medium text-gray-700">
                  Phone Number
                </label>
                <input
                  type="text"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
                  placeholder="e.g., 234567890"
                  required
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  id="active"
                />
                <label htmlFor="active" className="font-medium text-gray-700">
                  Active
                </label>
              </div>
              <button
                type="submit"
                className="w-full py-2 px-4 bg-blue-600 text-white font-semibold rounded hover:bg-blue-700 transition duration-200"
              >
                {editRecipientMode ? "Update Recipient" : "Add Recipient"}
              </button>
              {editRecipientMode && (
                <button
                  type="button"
                  onClick={handleCancelRecipientEdit}
                  className="w-full py-2 px-4 bg-gray-600 text-white font-semibold rounded hover:bg-gray-700 transition duration-200 mt-2"
                >
                  Cancel Edit
                </button>
              )}
            </form>

            <h3 className="text-lg font-bold mb-4 text-center">
              All Recipients
            </h3>
            {recipients.length === 0 ? (
              <p className="text-center text-gray-500">No recipients found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="border-collapse w-full">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-2 text-left">ID</th>
                      <th className="border p-2 text-left">Country Code</th>
                      <th className="border p-2 text-left">Phone Number</th>
                      <th className="border p-2 text-left">Active</th>
                      <th className="border p-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.map((recipient) => (
                      <tr key={recipient.id}>
                        <td className="border p-2">{recipient.id}</td>
                        <td className="border p-2">{recipient.country_code}</td>
                        <td className="border p-2">{recipient.phone_number}</td>
                        <td className="border p-2">
                          {recipient.active === 1 ? "Yes" : "No"}
                        </td>
                        <td className="border p-2 flex space-x-2 justify-center">
                          <button
                            onClick={() => handleEditRecipient(recipient)}
                            className="bg-yellow-500 text-white px-2 py-1 rounded hover:bg-yellow-600"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteRecipient(recipient.id)}
                            className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* WhatsApp QR Section */}
      <div className="p-4">
        <button
          onClick={handleShowQr}
          className="mb-4 bg-blue-500 text-white p-2 rounded"
        >
          {authenticated === true ? "Refresh Status" : "Show WhatsApp QR"}
        </button>

        {authenticated === null && (
          <p>Click "Show WhatsApp QR" to check status.</p>
        )}

        {authenticated === false && qrValue && (
          <div>
            <p>Scan this QR code with your WhatsApp to log in:</p>
            <QRCodeSVG value={qrValue} />
          </div>
        )}

        {authenticated === false && !qrValue && (
          <p>Initializing... or no QR available. Try again.</p>
        )}

        {authenticated === true && (
          <div>
            <p>You are logged in to WhatsApp.</p>
            <button
              onClick={handleLogout}
              className="bg-red-500 text-white p-2 rounded"
            >
              Logout
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default App;
